import React, { useEffect, useMemo, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { VERTEX_API_BASE } from '../../../utils/vertexApiHelper';

const SIMULATION_TYPES = [
  { value: 'negotiation', label: 'negotiation' },
  { value: 'leadership', label: 'leadership' },
  { value: 'conflict', label: 'conflict' },
  { value: 'evaluation', label: 'evaluation' },
  { value: 'other', label: 'other' }
];

const isBlank = (v) => typeof v !== 'string' || v.trim().length === 0;

const SIMULATION_TYPE_HELP = {
  negotiation: 'Interacción con oposición, presión y trade-offs verificables.',
  leadership: 'Conversaciones de influencia, feedback, dirección o toma de decisiones.',
  conflict: 'Escenarios de tensión emocional, desacuerdo o escalamiento.',
  evaluation: 'Evaluación estructurada del desempeño del participante.',
  other: 'Modo libre. No incluye coach ni moderador automáticos.'
};

const shouldIncludeCoachPrompt = (simulationType) => simulationType !== 'other';

// Regla aprobada (determinista):
// - negotiation/leadership/conflict: coach ✅, moderator ✅
// - evaluation: coach ✅, moderator ⚠️ opcional (se incluye en la tabla con nota)
// - other: ambos ❌
const shouldIncludeModeratorPrompt = (simulationType) => simulationType !== 'other';

function buildPromptRows({ simulationType, clientRole, userRole, pedagogicalObjective, phaseCount }) {
  const safePhaseCount = Math.max(1, Number(phaseCount) || 1);
  const includeCoach = shouldIncludeCoachPrompt(simulationType);
  const includeModerator = shouldIncludeModeratorPrompt(simulationType);

  const rows = [];

  rows.push({
    promptId: 'SYSTEM_PROMPT_BASE',
    promptType: 'base',
    destination: 'systemPrompt',
    rules: [
      'Debe establecer el marco global, sin redefinir fases.',
      'No incluir identidad detallada del cliente (eso va en Role Definition).',
      'No incluir prompts de coach/moderación.'
    ],
    promptText: [
      'Eres un simulador conversacional para entrenamiento.',
      `TIPO DE SIMULACIÓN: ${simulationType}`,
      `OBJETIVO PEDAGÓGICO: ${pedagogicalObjective}`,
      '',
      'Reglas globales:',
      '- Mantén coherencia de rol en todo momento.',
      '- No uses Markdown si el contrato del build requiere JSON estricto en prompts auxiliares.',
      '- Evita redundancias: cada bloque de prompt tiene una responsabilidad.'
    ].join('\n')
  });

  rows.push({
    promptId: 'CLIENT_ROLE_DEFINITION',
    promptType: 'base',
    destination: 'clientConfig.roleDefinition',
    rules: [
      'Identidad pura del cliente IA (sin preguntas, sin fases, sin arranques).',
      'No incluir instrucciones de coaching ni moderación.',
      'No incluir “primera pregunta” (eso se gestiona con BRIEF/State Machine).' 
    ],
    promptText: [
      'Identidad del Cliente IA:',
      `- Rol del cliente (contraparte): ${clientRole}`,
      `- Rol del usuario (humano): ${userRole}`,
      '',
      'Reglas:',
      '- Mantén un tono profesional, exigente y coherente con el rol.',
      '- No hagas preguntas en este bloque.',
      '- No describas fases, turnos, ni el arranque del diálogo.'
    ].join('\n')
  });

  rows.push({
    promptId: 'CLIENT_BEHAVIOR_INSTRUCTIONS',
    promptType: 'base',
    destination: 'clientConfig.behaviorInstructions',
    rules: [
      'Solo comportamiento por turno (estilo, presión, reglas).',
      'No redefinir identidad (eso va en Role Definition).',
      'Puede incluir heurísticas de preguntas por turno.'
    ],
    promptText: [
      'Instrucciones de comportamiento del Cliente IA:',
      '- Haz 1–2 preguntas concretas por turno cuando corresponda.',
      '- Mantén el foco en el objetivo pedagógico del entrenamiento.',
      '- Si falta información, pide aclaraciones antes de aceptar supuestos.',
      '- Evita contradicciones con el Role Definition.'
    ].join('\n')
  });

  // BRIEF: se entrega como 2 bloques que deben concatenarse en el MISMO campo.
  rows.push({
    promptId: 'BRIEF_ESCENARIO',
    promptType: 'base',
    destination: 'coachConfig.briefing.prompt (bloque 1 de 2)',
    rules: [
      'Pegar este bloque primero dentro de coachConfig.briefing.prompt.',
      'Este bloque NO debe incluir instrucciones de fases ni de identidad del cliente.',
      'Mantener salida JSON estricta (sin Markdown, sin texto extra).' 
    ],
    promptText: [
      'Eres un narrador que genera el BRIEF inicial del escenario.',
      '',
      'Devuelve EXCLUSIVAMENTE un objeto JSON (sin Markdown, sin backticks, sin texto extra).',
      'No agregues campos adicionales fuera del schema indicado.',
      '',
      'Keys obligatorios (inclúyelos tal cual):',
      '- escenario.texto',
      '',
      'Contenido:',
      `- Tipo de simulación: ${simulationType}`,
      `- Rol de contraparte: ${clientRole}`,
      `- Objetivo pedagógico: ${pedagogicalObjective}`,
      '',
      'Genera un brief realista y breve (4–8 líneas) dentro de escenario.texto.'
    ].join('\n')
  });

  rows.push({
    promptId: 'BRIEF_PREGUNTA',
    promptType: 'base',
    destination: 'coachConfig.briefing.prompt (bloque 2 de 2)',
    rules: [
      'Pegar este bloque inmediatamente debajo de BRIEF_ESCENARIO (mismo campo).',
      'La pregunta debe iniciar el diálogo, sin re-explicar el escenario.',
      'Mantener el schema canónico con pregunta.texto.'
    ],
    promptText: [
      'Además, incluye la key:',
      '- pregunta.texto',
      '',
      'Ejemplo de estructura esperada:',
      '{',
      '  "escenario": {',
      '    "texto": "..."',
      '  },',
      '  "pregunta": {',
      '    "texto": "..."',
      '  }',
      '}',
      '',
      'La pregunta debe ser directa y abrir conversación (1 sola pregunta).'
    ].join('\n')
  });

  for (let i = 1; i <= safePhaseCount; i += 1) {
    const idx = i - 1;

    rows.push({
      promptId: `PHASE_${i}_ON_ENTER`,
      promptType: 'phase',
      destination: `stateMachine.phases[${idx}].onEnterPrompt`,
      rules: [
        'Solo instrucciones de entrada a la fase (arranque de fase).',
        'No redefinir identidad del cliente.',
        i === 1 ? 'Si existe pregunta.texto del BRIEF, úsala como primera pregunta (sin duplicarla).' : 'Retomar desde la fase anterior sin reiniciar el escenario.'
      ],
      promptText: i === 1
        ? [
            'Entrada de fase 1:',
            '- Inicia usando la pregunta inicial del BRIEF (pregunta.texto).',
            '- Si no existe BRIEF, formula 1 pregunta inicial alineada al objetivo pedagógico.',
            '- No introduzcas nuevas reglas globales aquí.'
          ].join('\n')
        : [
            `Entrada de fase ${i}:`,
            '- Retoma el contexto acumulado y establece foco de la fase.',
            '- Formula una pregunta que abra la fase y avance el objetivo.'
          ].join('\n')
    });

    rows.push({
      promptId: `PHASE_${i}_PHASE_PROMPT`,
      promptType: 'phase',
      destination: `stateMachine.phases[${idx}].phasePrompt`,
      rules: [
        'Táctica exclusiva de la fase (no identidad, no BRIEF).',
        'Debe permitir conversación multi-turn: incluye preguntas cuando corresponda.',
        'Evitar frases tipo “no preguntes”.'
      ],
      promptText: [
        `Prompt principal de fase ${i}:`,
        `- Enfoca la conversación en avanzar el objetivo: ${pedagogicalObjective}`,
        '- Mantén tensión/realismo acorde a la simulación.',
        '- Haz 1–2 preguntas concretas por turno para sostener el diálogo.'
      ].join('\n')
    });

    rows.push({
      promptId: `PHASE_${i}_ON_EXIT`,
      promptType: 'phase',
      destination: `stateMachine.phases[${idx}].onExitPrompt`,
      rules: [
        'Cierre de fase: síntesis + puente a la siguiente fase.',
        'No abrir nuevos temas grandes al cerrar.',
        'Preparar transición (si hay siguiente fase).' 
      ],
      promptText: [
        `Salida de fase ${i}:`,
        '- Resume en 2–4 líneas lo acordado o lo pendiente.',
        '- Deja un puente claro hacia el siguiente foco (si aplica).',
        '- Termina con una pregunta corta que prepare la transición.'
      ].join('\n')
    });
  }

  if (includeCoach) {
    rows.push({
      promptId: 'COACH_PROMPT_BASE',
      promptType: 'aux',
      destination: 'coachConfig.promptBase',
      rules: [
        'Aislado del cliente: no habla como cliente.',
        'Salida JSON estricta con schema fijo.',
        'Feedback breve, accionable y alineado al objetivo.'
      ],
      promptText: [
        '# IA COACH (EVALUADOR) — CONTRATO JSON V1',
        '',
        'Eres un coach evaluador. Observas la interacción y, cuando corresponda, entregas feedback breve y accionable.',
        `OBJETIVO PEDAGÓGICO: ${pedagogicalObjective}`,
        `TIPO DE SIMULACIÓN: ${simulationType}`,
        '',
        'Devuelve EXCLUSIVAMENTE un objeto JSON.',
        '- No uses Markdown',
        '- No incluyas backticks',
        '- No escribas texto fuera del JSON',
        '- No agregues campos adicionales',
        '',
        'Keys obligatorios:',
        '- hasSuggestion: boolean',
        '- suggestion: string',
        '- type: string',
        '- reason: string',
        '- action: string',
        '',
        'Ejemplo mínimo:',
        '{',
        '  "hasSuggestion": true,',
        '  "suggestion": "...",',
        '  "type": "...",',
        '  "reason": "...",',
        '  "action": "..."',
        '}'
      ].join('\n')
    });
  }

  if (includeModerator) {
    rows.push({
      promptId: 'MODERATOR_PROMPT_BASE',
      promptType: 'aux',
      destination: 'moderatorConfig.promptBase',
      rules: [
        'Aislado del cliente: no negocia ni evalúa técnica.',
        'Detecta spam/ofensivo/bloqueo conversacional.',
        simulationType === 'evaluation'
          ? 'Opcional en evaluation: puede omitirse si el flujo es solo scoring post-hoc.'
          : 'Salida JSON estricta con schema fijo.'
      ],
      promptText: [
        '# MODERADOR AUTOMÁTICO — CONTRATO JSON V1',
        '',
        `Supervisas la integridad conversacional del simulador (${simulationType}).`,
        'Tu única función es detectar comportamiento inapropiado o bloqueo conversacional.',
        '',
        'Devuelve EXCLUSIVAMENTE un objeto JSON.',
        '- No uses Markdown',
        '- No incluyas backticks',
        '- No escribas texto fuera del JSON',
        '- No agregues campos adicionales',
        '',
        'Keys obligatorios:',
        '- needsIntervention: boolean',
        '- reason: string',
        '- severity: string',
        '- action: string',
        '- suggestHelp: boolean',
        '- message: string',
        '',
        'Ejemplo mínimo:',
        '{',
        '  "needsIntervention": false,',
        '  "reason": "ok",',
        '  "severity": "none",',
        '  "action": "none",',
        '  "suggestHelp": false,',
        '  "message": ""',
        '}'
      ].join('\n')
    });
  }

  return rows;
}

function runSemanticQa(rows) {
  const findings = [];

  const byId = new Map(rows.map((r) => [r.promptId, r]));
  const roleDef = byId.get('CLIENT_ROLE_DEFINITION')?.promptText || '';

  // Warn: RoleDefinition contiene preguntas
  if (/[¿?]/.test(roleDef)) {
    findings.push('RoleDefinition contiene signos de pregunta. Este bloque no debe incluir preguntas.');
  }

  // Warn: Prompt de fase redefine identidad
  for (const r of rows) {
    if (r.promptType !== 'phase') continue;
    if (/\bEres\b/i.test(r.promptText)) {
      findings.push(`${r.promptId}: contiene “Eres”. Evita redefinir identidad dentro de prompts de fase.`);
      break;
    }
  }

  // Warn: Brief incluye instrucciones de coach
  const briefText = `${byId.get('BRIEF_ESCENARIO')?.promptText || ''}\n${byId.get('BRIEF_PREGUNTA')?.promptText || ''}`.toLowerCase();
  if (/(coach|evaluador|feedback|metodolog)/i.test(briefText)) {
    findings.push('BRIEF incluye términos de coach/feedback. Mantén BRIEF como narrativa + pregunta inicial.');
  }

  // Warn: Mezcla identidad + táctica en RoleDefinition
  if (/(fase|phases?|state machine|onenter|onexit|turnos?)/i.test(roleDef)) {
    findings.push('RoleDefinition menciona fases/turnos. Este bloque debe ser identidad pura del cliente.');
  }

  const status = findings.length > 0 ? 'WARN' : 'OK';
  return { status, findings };
}

function buildComposerOutputV1FromRows(rows, inputs) {
  const byId = new Map(rows.map((r) => [r.promptId, r]));

  const roleDefinition_base = byId.get('CLIENT_ROLE_DEFINITION')?.promptText || '';
  const briefTemplate = [
    byId.get('BRIEF_ESCENARIO')?.promptText || '',
    byId.get('BRIEF_PREGUNTA')?.promptText || ''
  ].filter(Boolean).join('\n\n');

  const phaseCount = Math.max(1, Number(inputs?.phaseCount) || 1);
  const phasePrompts = [];
  for (let i = 1; i <= phaseCount; i += 1) {
    phasePrompts.push({
      phaseId: `PHASE_${i}`,
      onEnterPrompt: byId.get(`PHASE_${i}_ON_ENTER`)?.promptText || '',
      phasePrompt: byId.get(`PHASE_${i}_PHASE_PROMPT`)?.promptText || '',
      onExitPrompt: byId.get(`PHASE_${i}_ON_EXIT`)?.promptText || ''
    });
  }

  return {
    roleDefinition_base,
    brief_schema: {
      requiredKeys: ['escenario.texto', 'pregunta.texto'],
      template: briefTemplate
    },
    phasePrompts,
    coach_prompt_base: byId.get('COACH_PROMPT_BASE')?.promptText || '',
    moderator_prompt_base: byId.get('MODERATOR_PROMPT_BASE')?.promptText || '',
    metadata: {
      simulationType: inputs?.simulationType,
      phaseCount
    }
  };
}

export default function PromptComposerTab() {
  const { token } = useAuth();

  const [inputs, setInputs] = useState({
    simulationType: 'negotiation',
    clientRole: '',
    userRole: '',
    pedagogicalObjective: '',
    phaseCount: 2
  });

  const [copiedId, setCopiedId] = useState(null);
  const [copyError, setCopyError] = useState(null);

  const isValid = useMemo(() => {
    return (
      !isBlank(inputs.simulationType) &&
      !isBlank(inputs.clientRole) &&
      !isBlank(inputs.userRole) &&
      !isBlank(inputs.pedagogicalObjective) &&
      Number(inputs.phaseCount) >= 1
    );
  }, [inputs]);

  const rows = useMemo(() => {
    if (!isValid) return [];
    return buildPromptRows(inputs);
  }, [inputs, isValid]);

  const qa = useMemo(() => {
    if (!isValid) return { status: 'OK', findings: [] };
    return runSemanticQa(rows);
  }, [rows, isValid]);

  const [buildQaState, setBuildQaState] = useState({
    status: 'IDLE',
    errors: [],
    warnings: [],
    lastError: null
  });
  const [buildQaLoading, setBuildQaLoading] = useState(false);

  const composerOutputV1 = useMemo(() => {
    if (!isValid) return null;
    return buildComposerOutputV1FromRows(rows, inputs);
  }, [rows, inputs, isValid]);

  useEffect(() => {
    if (!isValid || !composerOutputV1) {
      setBuildQaState({ status: 'IDLE', errors: [], warnings: [], lastError: null });
      setBuildQaLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setBuildQaLoading(true);

        const response = await fetch(`${VERTEX_API_BASE}/v4/composer/normalize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            rawInputs: {
              simulationType: inputs.simulationType,
              clientRole: inputs.clientRole,
              userRole: inputs.userRole,
              pedagogicalObjective: inputs.pedagogicalObjective,
              phaseCount: inputs.phaseCount
            },
            composerOutput: composerOutputV1
          }),
          signal: controller.signal
        });

        const data = await response.json().catch(() => ({}));
        const qaFromServer = data?.qa || null;

        if (!qaFromServer) {
          setBuildQaState({ status: 'ERROR', errors: [], warnings: [], lastError: 'Respuesta QA inválida.' });
          return;
        }

        const serverStatus = (qaFromServer.status || '').toLowerCase();
        const errors = Array.isArray(qaFromServer.errors) ? qaFromServer.errors : [];
        const warnings = Array.isArray(qaFromServer.warnings) ? qaFromServer.warnings : [];

        if (!response.ok || serverStatus === 'fail') {
          setBuildQaState({ status: 'FAIL', errors, warnings, lastError: null });
          return;
        }

        setBuildQaState({ status: warnings.length > 0 ? 'WARN' : 'OK', errors: [], warnings, lastError: null });
      } catch (e) {
        if (e?.name === 'AbortError') return;
        setBuildQaState({ status: 'ERROR', errors: [], warnings: [], lastError: 'No se pudo ejecutar Build QA.' });
      } finally {
        setBuildQaLoading(false);
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [isValid, composerOutputV1, token, inputs]);

  const onChange = (key) => (e) => {
    const nextValue = e.target.value;
    setInputs((prev) => ({ ...prev, [key]: key === 'phaseCount' ? Number(nextValue) : nextValue }));
  };

  const copyPrompt = async (row) => {
    try {
      setCopyError(null);
      await navigator.clipboard.writeText(row.promptText);
      setCopiedId(row.promptId);
      setTimeout(() => setCopiedId(null), 1200);
    } catch (e) {
      setCopyError('No se pudo copiar al portapapeles.');
    }
  };

  const qaStyles = {
    OK: 'bg-green-100 text-green-800 border-green-200',
    WARN: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    FAIL: 'bg-red-100 text-red-800 border-red-200',
    ERROR: 'bg-red-100 text-red-800 border-red-200',
    IDLE: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  const typeBadge = (t) => {
    if (t === 'base') return 'bg-slate-100 text-slate-700';
    if (t === 'phase') return 'bg-indigo-100 text-indigo-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Prompt Composer</h2>
        <p className="text-sm text-gray-600">
          Genera prompts canónicos en solo lectura. Copia y pega manualmente en el Builder (no guarda ni aplica cambios).
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de simulación</label>
            <select
              value={inputs.simulationType}
              onChange={onChange('simulationType')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {SIMULATION_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <p className="text-xs text-gray-600 mt-2">
              Define el tipo de interacción conversacional, no la habilidad a entrenar. Ejemplo: inteligencia emocional puede
              entrenarse en negotiation, leadership o conflict según el escenario.
            </p>

            <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
              {SIMULATION_TYPE_HELP[inputs.simulationType] || ''}
            </div>

            {inputs.simulationType === 'other' && (
              <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
                En modo other, Coach y Moderador se desactivan para permitir experimentación libre.
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">phaseCount (&gt;= 1)</label>
            <input
              type="number"
              min={1}
              value={inputs.phaseCount}
              onChange={onChange('phaseCount')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">clientRole</label>
            <input
              type="text"
              value={inputs.clientRole}
              onChange={onChange('clientRole')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Ej: Director(a) de Compras de una empresa retail"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">userRole</label>
            <input
              type="text"
              value={inputs.userRole}
              onChange={onChange('userRole')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Ej: Account Executive de un proveedor SaaS"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">pedagogicalObjective</label>
          <textarea
            value={inputs.pedagogicalObjective}
            onChange={onChange('pedagogicalObjective')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Ej: Practicar diagnóstico, preguntas de descubrimiento y propuesta con trade-offs"
          />
        </div>

        {!isValid && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
            Completa los 5 inputs para generar la tabla de prompts.
          </div>
        )}
      </div>

      {isValid && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">Build QA (Composer, pre-copiado)</h3>
            <div className="flex items-center gap-2">
              {buildQaLoading && (
                <span className="text-xs text-gray-500">Validando…</span>
              )}
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${qaStyles[buildQaState.status] || qaStyles.IDLE}`}>
                {buildQaState.status}
              </span>
            </div>
          </div>

          {buildQaState.status === 'FAIL' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <LucideIcons.AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm text-red-800 font-medium">FAIL (P0): no usable para exportar/usar.</p>
                <p className="text-sm text-red-700">Puedes copiar manualmente, pero corrige estos puntos antes de reutilizar los prompts.</p>
              </div>
            </div>
          )}

          {buildQaState.lastError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <LucideIcons.AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{buildQaState.lastError}</p>
            </div>
          )}

          {buildQaState.errors.length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
              {buildQaState.errors.map((e, idx) => (
                <li key={idx}>
                  <span className="font-mono text-xs">{e.checkId}</span>: {e.message}
                </li>
              ))}
            </ul>
          )}

          {buildQaState.warnings.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
              {buildQaState.warnings.map((w, idx) => (
                <li key={idx}>
                  <span className="font-mono text-xs">{w.checkId}</span>: {w.message}
                </li>
              ))}
            </ul>
          ) : (buildQaState.status === 'OK' ? (
            <p className="text-sm text-gray-600">Sin warnings detectados.</p>
          ) : null)}
        </div>
      )}

      {isValid && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">QA semántico (no bloqueante)</h3>
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${qaStyles[qa.status] || qaStyles.OK}`}>
              {qa.status}
            </span>
          </div>

          {qa.findings.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
              {qa.findings.map((f, idx) => (
                <li key={idx}>{f}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600">Sin warnings detectados.</p>
          )}

          {copyError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <LucideIcons.AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{copyError}</p>
            </div>
          )}
        </div>
      )}

      {isValid && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Tabla de prompts canónicos</h3>
            <p className="text-xs text-gray-600 mt-1">Ordenados de arriba hacia abajo según precedencia sugerida.</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destino recomendado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Texto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reglas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row) => (
                  <tr key={row.promptId} className="align-top">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm font-semibold text-gray-900">{row.promptId}</div>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${typeBadge(row.promptType)}`}>
                          {row.promptType}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="text-xs text-gray-700 font-mono break-all">{row.destination}</div>
                    </td>

                    <td className="px-4 py-4">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap max-w-[48rem]">{row.promptText}</pre>
                    </td>

                    <td className="px-4 py-4">
                      <ul className="list-disc pl-5 space-y-1 text-xs text-gray-700">
                        {row.rules.map((r, idx) => (
                          <li key={idx}>{r}</li>
                        ))}
                      </ul>
                    </td>

                    <td className="px-4 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => copyPrompt(row)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        <LucideIcons.Copy className="w-4 h-4" />
                        {copiedId === row.promptId ? 'Copiado' : 'Copiar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
